/**
 * WebGL availability check + fallback error screen.
 *
 * Run before Phaser boots. The game's timing-sensitive systems (scroll
 * cadence, spider patrols, water animation) degrade noticeably under the
 * software-rendered Canvas 2D path, so we refuse to start at all rather
 * than ship a worse-than-broken experience. If WebGL is missing or fails
 * to initialize, we render a friendly explanation in place of the canvas.
 */

const PARENT_ID = 'game';

/**
 * Probe for WebGL support. Creates a throwaway canvas and tries to acquire a
 * WebGL context. Some browsers return a non-null but unusable stub — we
 * sanity-check that getParameter exists before declaring success.
 *
 * Returns true if WebGL is available; false on any failure path. Never throws.
 */
export function isWebGLAvailable(): boolean {
  try {
    if (typeof document === 'undefined') return false;
    const probe = document.createElement('canvas');
    // 'webgl' is the standard name; 'experimental-webgl' covers older browsers.
    const gl =
      (probe.getContext('webgl') as WebGLRenderingContext | null) ??
      (probe.getContext('experimental-webgl') as WebGLRenderingContext | null);
    if (!gl) return false;
    // A real context exposes getParameter; stubs may not.
    if (typeof gl.getParameter !== 'function') return false;
    return true;
  } catch {
    return false;
  }
}

/**
 * Render the WebGL-required error screen into the game container. Styled to
 * match the rest of the game — dark background, monospace, gold accent —
 * so it doesn't look like a generic browser error.
 */
export function showWebGLError(): void {
  if (typeof document === 'undefined') return;
  const parent = document.getElementById(PARENT_ID);
  if (!parent) return;

  // Clear anything Phaser might have left behind from a failed init.
  parent.innerHTML = '';

  const wrap = document.createElement('div');
  wrap.style.cssText = [
    'width: 100%',
    'height: 100%',
    'min-height: 100vh',
    'background: #16161a',
    'color: #e8e8e8',
    'font-family: monospace',
    'display: flex',
    'flex-direction: column',
    'align-items: center',
    'justify-content: center',
    'padding: 24px',
    'box-sizing: border-box',
    'text-align: center',
  ].join('; ');

  const title = document.createElement('div');
  title.textContent = '🥥 Splat Chimp';
  title.style.cssText = [
    'font-size: 28px',
    'font-weight: bold',
    'color: #e8c87a',
    'margin-bottom: 24px',
  ].join('; ');

  const heading = document.createElement('div');
  heading.textContent = 'Your browser needs WebGL to play this game.';
  heading.style.cssText = [
    'font-size: 16px',
    'line-height: 1.5',
    'max-width: 480px',
    'margin-bottom: 20px',
  ].join('; ');

  const body = document.createElement('div');
  body.innerHTML = [
    'Most modern browsers support WebGL by default, but it may be',
    'turned off on your device. To play, try:',
  ].join('<br>');
  body.style.cssText = [
    'font-size: 13px',
    'color: #b0b0b0',
    'line-height: 1.6',
    'max-width: 480px',
    'margin-bottom: 16px',
  ].join('; ');

  const list = document.createElement('ul');
  list.style.cssText = [
    'font-size: 13px',
    'color: #d0d0d0',
    'text-align: left',
    'line-height: 1.7',
    'list-style: none',
    'padding: 0',
    'margin: 0 0 24px 0',
    'max-width: 420px',
  ].join('; ');
  const items = [
    'Turn on <b>hardware acceleration</b> in your browser settings',
    'Update your <b>graphics drivers</b>',
    'Try a different browser (Chrome, Firefox, Edge, or Safari)',
    'Disable any extensions that block WebGL',
  ];
  for (const html of items) {
    const li = document.createElement('li');
    li.innerHTML = `• ${html}`;
    list.appendChild(li);
  }

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = '↻ RELOAD PAGE';
  btn.style.cssText = [
    'font-family: monospace',
    'font-size: 14px',
    'font-weight: bold',
    'padding: 10px 20px',
    'background: #2a8a4a',
    'color: #ffffff',
    'border: 2px solid rgba(255,255,255,0.25)',
    'border-radius: 4px',
    'cursor: pointer',
    'touch-action: manipulation',
  ].join('; ');
  btn.addEventListener('click', () => {
    window.location.reload();
  });

  const small = document.createElement('div');
  small.textContent = 'If the issue persists after trying these steps, the device may not support the game.';
  small.style.cssText = [
    'font-size: 11px',
    'color: #707070',
    'margin-top: 24px',
    'max-width: 420px',
    'line-height: 1.5',
  ].join('; ');

  wrap.appendChild(title);
  wrap.appendChild(heading);
  wrap.appendChild(body);
  wrap.appendChild(list);
  wrap.appendChild(btn);
  wrap.appendChild(small);
  parent.appendChild(wrap);
}
